import React from "react";
import Styles from "./OnlineTruckBooking.module.scss";
import {
    useOtbAchievement,
    useOtbFeatures,
    useOtbHowItworkData,
    useLorryTypesData,
    useOtbServiceFeature
} from "../service-data/ServiceData";
import { FaqsResponse } from "@vahak/core/dist/_services/faq.service";
import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";

import Features from "../common/features/Features";
import Achievements from "../common/achievements/Achievements";
import HeroSection from "../common/hero-section/HeroSection";
import HowItWorks from "../common/how-it-works/HowItWorks";
import Faq from "../common/faq/Faq";
import TypesOfLorries from "../types-of-lorries/TypesOfLorries";
import { useTranslation } from "next-i18next";
import { GA4EventNames, GA4ServicePageOnlineTruckBookingEventValues } from "@vahak/core/dist/constants/event-names";
import { SOURCE_UTM } from "@vahak/core/dist/constants/misc";
import { PageTypes } from "@vahak/core/dist/constants/Pages";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import { breadcrumbsConstants } from "../../../constants/breadcrumbs-constants";
import ServiceInformation from "../common/service-information/ServiceInformation";
import GenericTitleDescriptionWithParas from "../../generic-title-description-with-paras/GenericTitleDescriptionWithParas";

interface OnlineTruckBookinProps {
    faqs: FaqsResponse[];
}

const metaData = {
    title: "Online Truck Booking in India, Truck Rental Services | Vahak",
    description:
        "Find the right truck for your business through India's largest online truck booking community. Hire trucks, containers, and more with 0% commission.",
    keywords:
        "truck booking,truck transport, lorry transport, online truck booking, truck rental, truck booking app, lorry transport services, truck transportation services, lorry service near me, online mini truck booking, truck rental near me, mini truck booking, truck rental services, book a lorry, lorry booking,mini truck transport near me, online truck booking app, hire truck online, truck booking price, trucks portal online, online goods vehicle booking, book a truck for shifting, truck booking india, truck for hire near me, book transport truck, app for truck booking, online truck booking system, online truck service,",
    canonicalUrl: "https://www.vahak.in/online-truck-booking",
    schemaOrgJsonUrl: "/online-truck-booking",
    ogImgSrc: "https://d3ib4ot6oar1ep.cloudfront.net/1080/75/webp/online-truck-booking-app_desktop.png",
    ogImgAltText: "Online Truck Booking",
    breadcrumbList: breadcrumbsConstants({}).onlineLorryBooking
};

const OnlineTruckBooking = ({ faqs }: OnlineTruckBookinProps) => {
    const { t } = useTranslation("servicePages");

    return (
        <>
            <PageMetaTags
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
                canonicalUrl={metaData.canonicalUrl}
                ogUrl={metaData.canonicalUrl}
                ogImgSrc={metaData.ogImgSrc}
                ogImgAltText={metaData.ogImgAltText}
                schemaOrgJsonUrl={metaData.schemaOrgJsonUrl}
                breadcrumbList={metaData.breadcrumbList}
            />

            <main className={Styles.main}>
                <HeroSection
                    bannerTitle={t("servicePages:online-truck-booking.hero-section.title")}
                    bannerText={t("servicePages:online-truck-booking.hero-section.bannerText")}
                    hrefLabel={t("servicePages:online-truck-booking.hero-section.hrefLabel")}
                    hrefURL={`${ROUTES.marketplace.getUrl("lorry")}?${SOURCE_UTM}=${PageTypes.ONLINE_TRUCK_BOOKING}`}
                    onLinkClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_truck_booking, {
                            value: GA4ServicePageOnlineTruckBookingEventValues.book_trucks_now
                        });
                    }}
                    // srcSetURL={`${IMG_BASE_URL}/online-truck-booking-app_desktop_3x.png 1587w, ${IMG_BASE_URL}/online-truck-booking-app_desktop_2x.png 1058w, ${IMG_BASE_URL}/online-truck-booking-app_desktop.png 529w, ${IMG_BASE_URL}/online-truck-booking-app_mobile_3x.png 828w, ${IMG_BASE_URL}/online-truck-booking-app_mobile_2x.png 552w, ${IMG_BASE_URL}/online-truck-booking-app_mobile.png 276w`}
                    srcURL={`online-truck-booking-app_desktop.png`}
                    altText="vahak online truck booking app with lorries"
                />

                <Achievements
                    achievementData={useOtbAchievement()}
                    sectionTitle={t("servicePages:online-truck-booking.achievements.sectionTitle")}
                />

                <ServiceInformation
                    heading={t("servicePages:online-truck-booking.features-of-vahak-truck-booking-service.heading")}
                    sectionTitle=""
                    sectionText={t(
                        "servicePages:online-truck-booking.features-of-vahak-truck-booking-service.description"
                    )}
                    descriptionComponent={
                        <>
                            <GenericTitleDescriptionWithParas
                                titleDescriptionList={useOtbServiceFeature()}
                                heading={""}
                            />
                        </>
                    }
                />

                <Features
                    features={useOtbFeatures()}
                    sectionTitle={t("servicePages:online-truck-booking.features.sectionTitle")}
                    ctaLink={`${ROUTES.marketplace.getUrl("lorry")}`}
                    ctaLabel={t("servicePages:online-truck-booking.features.ctaLabel")}
                    onCtaClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_truck_booking, {
                            value: GA4ServicePageOnlineTruckBookingEventValues.start_booking_now
                        });
                    }}
                />

                <HowItWorks
                    howitwork={useOtbHowItworkData()}
                    sectionTitle={t("servicePages:online-truck-booking.HowItWorks.sectionTitle")}
                    sectionText={t("servicePages:online-truck-booking.HowItWorks.sectionText")}
                    tabAppNav={t("servicePages:online-load-booking.HowItWorks.tabAppNav")}
                    tabWebNav={t("servicePages:online-load-booking.HowItWorks.tabWebNav")}
                    onAppTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_truck_booking, {
                            value: GA4ServicePageOnlineTruckBookingEventValues.mobile_app
                        });
                    }}
                    onWebTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_truck_booking, {
                            value: GA4ServicePageOnlineTruckBookingEventValues.web
                        });
                    }}
                />

                <TypesOfLorries
                    lorryTypes={useLorryTypesData()}
                    sectionTitle={t("servicePages:online-truck-booking.TypesOfLorries.sectionTitle")}
                    sectionText={t("servicePages:online-truck-booking.TypesOfLorries.sectionText")}
                />

                <Faq
                    faqs={faqs}
                    sectionTitle={t("servicePages:online-load-booking.Faq.sectionTitle")}
                    sectionText={t("servicePages:online-load-booking.Faq.sectionText")}
                />
            </main>
        </>
    );
};

export default OnlineTruckBooking;
